import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyIcon from '@mui/icons-material/ContentCopy';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Bot, BotToken, botService } from '../api/actors';
import { ActorChip } from './workflow/ActorChip';
import { formatAppDateTime } from '../utils/dateFormats';

const DEFAULT_ABILITIES = 'tasks:*, projects:*, tags:*, music:*, notes:read';

const parseAbilities = (raw: string): string[] =>
    raw.split(/[\s,]+/).map((item) => item.trim()).filter(Boolean);

const BotTokens = ({ bot }: { bot: Bot }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const queryKey = ['bot-tokens', bot.id];
    const [name, setName] = useState(`${bot.slug}-token`);
    const [abilities, setAbilities] = useState(DEFAULT_ABILITIES);
    const [plainToken, setPlainToken] = useState<string | null>(null);

    const { data: tokens = [], isLoading } = useQuery<BotToken[]>({ queryKey, queryFn: () => botService.tokens(bot.id) });

    const createMutation = useMutation({
        mutationFn: () => botService.createToken(bot.id, { name, abilities: parseAbilities(abilities) }),
        onSuccess: async (result) => {
            setPlainToken(result.token);
            await queryClient.invalidateQueries({ queryKey });
            await queryClient.invalidateQueries({ queryKey: ['bots'] });
        },
        onError: () => enqueueSnackbar(t('bots.saveError'), { variant: 'error' }),
    });

    const revokeMutation = useMutation({
        mutationFn: (tokenId: string) => botService.revokeToken(bot.id, tokenId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey });
            await queryClient.invalidateQueries({ queryKey: ['bots'] });
        },
    });

    return (
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            {isLoading ? <CircularProgress size={18} /> : tokens.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('bots.noTokens')}</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('bots.tokenName')}</TableCell>
                            <TableCell>{t('bots.abilities')}</TableCell>
                            <TableCell>{t('bots.lastActive')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tokens.map((token) => (
                            <TableRow key={token.id}>
                                <TableCell>{token.name}</TableCell>
                                <TableCell>
                                    {token.abilities.map((ability) => <Chip key={ability} size="small" label={ability} sx={{ mr: 0.5 }} />)}
                                </TableCell>
                                <TableCell>{token.last_used_at ? formatAppDateTime(new Date(token.last_used_at)) : t('bots.never')}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title={t('bots.revoke')}>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => window.confirm(t('bots.revokeConfirm')) && revokeMutation.mutate(token.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {bot.is_active && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 2 }} alignItems={{ md: 'flex-start' }}>
                    <TextField size="small" label={t('bots.tokenName')} value={name} onChange={(event) => setName(event.target.value)} />
                    <TextField
                        size="small"
                        label={t('bots.abilities')}
                        value={abilities}
                        onChange={(event) => setAbilities(event.target.value)}
                        helperText={t('bots.abilitiesHint')}
                        sx={{ flex: 1 }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<KeyIcon />}
                        onClick={() => createMutation.mutate()}
                        disabled={createMutation.isPending || parseAbilities(abilities).length === 0}
                    >
                        {t('bots.createToken')}
                    </Button>
                </Stack>
            )}

            <Dialog open={plainToken !== null} onClose={() => setPlainToken(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{bot.name}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>{t('bots.tokenCreated')}</Alert>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1, fontFamily: 'monospace' }}>{plainToken}</Typography>
                        <IconButton
                            onClick={() => {
                                if (plainToken) {
                                    void navigator.clipboard.writeText(plainToken);
                                    enqueueSnackbar(t('bots.copied'), { variant: 'success' });
                                }
                            }}
                        >
                            <CopyIcon />
                        </IconButton>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPlainToken(null)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/** Bot identities of the workspace: create, enable/disable, issue/revoke tokens. */
export const BotsPanel = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [expanded, setExpanded] = useState<number | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [draft, setDraft] = useState({ name: '', role: '', color: '#64748b' });

    const { data: bots = [], isLoading, error } = useQuery<Bot[]>({ queryKey: ['bots'], queryFn: botService.list });

    const refresh = async () => {
        await queryClient.invalidateQueries({ queryKey: ['bots'] });
        await queryClient.invalidateQueries({ queryKey: ['actors'] });
    };

    const createMutation = useMutation({
        mutationFn: () => botService.create({ name: draft.name.trim(), role: draft.role.trim() || null, color: draft.color || null }),
        onSuccess: async (bot) => {
            setCreateOpen(false);
            setDraft({ name: '', role: '', color: '#64748b' });
            setExpanded(bot.id);
            enqueueSnackbar(t('bots.created'), { variant: 'success' });
            await refresh();
        },
        onError: () => enqueueSnackbar(t('bots.saveError'), { variant: 'error' }),
    });

    const toggleMutation = useMutation({
        mutationFn: (bot: Bot) => (bot.is_active ? botService.deactivate(bot.id) : botService.update(bot.id, { is_active: true }).then(() => undefined)),
        onSuccess: refresh,
        onError: () => enqueueSnackbar(t('bots.saveError'), { variant: 'error' }),
    });

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h5">{t('bots.title')}</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                    {t('bots.create')}
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('bots.subtitle')}</Typography>

            {isLoading && <CircularProgress size={22} />}
            {error && <Alert severity="error">{t('common.errorFetchingData')}</Alert>}
            {!isLoading && bots.length === 0 && <Typography color="text.secondary">{t('bots.noBots')}</Typography>}

            {bots.length > 0 && (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('bots.name')}</TableCell>
                            <TableCell>{t('bots.role')}</TableCell>
                            <TableCell>{t('bots.tokens')}</TableCell>
                            <TableCell>{t('bots.lastActive')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bots.map((bot) => (
                            <Fragment key={bot.id}>
                                <TableRow hover sx={{ opacity: bot.is_active ? 1 : 0.55 }}>
                                    <TableCell>
                                        <ActorChip actor={{ key: bot.key, type: 'bot', id: bot.id, name: bot.name, color: bot.color, role: bot.role, is_active: bot.is_active }} />
                                        {!bot.is_active && <Chip size="small" label={t('bots.inactive')} sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell>{bot.role || '—'}</TableCell>
                                    <TableCell>{bot.active_tokens_count ?? 0}</TableCell>
                                    <TableCell>{bot.last_active_at ? formatAppDateTime(new Date(bot.last_active_at)) : t('bots.never')}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={bot.is_active ? t('bots.deactivate') : t('bots.reactivate')}>
                                            <IconButton
                                                size="small"
                                                color={bot.is_active ? 'error' : 'success'}
                                                onClick={() => {
                                                    if (!bot.is_active || window.confirm(t('bots.deactivateConfirm'))) {
                                                        toggleMutation.mutate(bot);
                                                    }
                                                }}
                                            >
                                                <PowerSettingsNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('bots.tokens')}>
                                            <IconButton size="small" onClick={() => setExpanded(expanded === bot.id ? null : bot.id)}>
                                                {expanded === bot.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ py: 0, borderBottom: expanded === bot.id ? undefined : 'none' }}>
                                        <Collapse in={expanded === bot.id} unmountOnExit>
                                            <Box sx={{ py: 1.5 }}><BotTokens bot={bot} /></Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('bots.create')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label={t('bots.name')} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required autoFocus />
                        <TextField label={t('bots.role')} value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
                        <TextField label={t('bots.color')} type="color" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" disabled={!draft.name.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
                        {t('bots.create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};
